/**
 * Asset Link GraphQL Queries and Mutations
 *
 * GraphQL operations for the asset trackable links and QR code system.
 */

import { gql } from "@apollo/client";

// Fragments
export const ASSET_LINK_FRAGMENT = gql`
  fragment AssetLinkFragment on AssetLink {
    id
    assetId
    tenantId
    trackableUrl
    qrTrackableUrl
    shortCode
    metadata {
      description
      tags
      customParameters
    }
    qrCodes {
      id
      assetLinkId
      svgContent
      downloadUrl
      size
      format
      cacheKey
      generatedAt
    }
    customQRCodes {
      id
      assetLinkId
      filename
      s3Key
      downloadUrl
      contentType
      size
      uploadedAt
    }
    clickCount
    scanCount
    lastClickedAt
    lastScannedAt
    status
    createdAt
    updatedAt
  }
`;

// Queries
export const GET_ASSET_LINK = gql`
  ${ASSET_LINK_FRAGMENT}
  query GetAssetLink($id: ID!) {
    getAssetLink(id: $id) {
      ...AssetLinkFragment
    }
  }
`;

export const LIST_ASSET_LINKS = gql`
  ${ASSET_LINK_FRAGMENT}
  query ListAssetLinks(
    $assetId: String!
    $filter: AssetLinkFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listAssetLinks(
      assetId: $assetId
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ...AssetLinkFragment
      }
      nextToken
      total
    }
  }
`;

export const GENERATE_QR_CODE_DOWNLOAD_URL = gql`
  query GenerateQRCodeDownloadUrl($qrCodeId: String!, $format: QRCodeFormat!) {
    generateQRCodeDownloadUrl(qrCodeId: $qrCodeId, format: $format)
  }
`;

export const GET_ASSET_LINK_ANALYTICS = gql`
  query GetAssetLinkAnalytics($assetId: String!) {
    getAssetLinkAnalytics(assetId: $assetId) {
      totalClicks
      uniqueClicks
      clicksByDevice {
        mobile
        desktop
        tablet
        other
      }
      clicksByLocation {
        country
        region
        city
        clicks
      }
      clicksByHour {
        hour
        clicks
        date
      }

      lastClickAt
    }
  }
`;

// Mutations
export const GENERATE_ASSET_LINK = gql`
  ${ASSET_LINK_FRAGMENT}
  mutation GenerateAssetLink($input: GenerateAssetLinkInput!) {
    generateAssetLink(input: $input) {
      ...AssetLinkFragment
    }
  }
`;

export const UPDATE_ASSET_LINK = gql`
  ${ASSET_LINK_FRAGMENT}
  mutation UpdateAssetLink($id: ID!, $input: UpdateAssetLinkInput!) {
    updateAssetLink(id: $id, input: $input) {
      ...AssetLinkFragment
    }
  }
`;

export const DELETE_ASSET_LINK = gql`
  mutation DeleteAssetLink($id: ID!) {
    deleteAssetLink(id: $id)
  }
`;

export const GENERATE_QR_CODE = gql`
  mutation GenerateQRCode($input: GenerateQRCodeInput!) {
    generateQRCode(input: $input) {
      qrCodeId
      downloadUrl
      format
      size
      createdAt
      expiresAt
    }
  }
`;

export const DELETE_QR_CODE = gql`
  mutation DeleteQRCode($id: ID!) {
    deleteQRCode(id: $id)
  }
`;

export const UPLOAD_CUSTOM_QR = gql`
  mutation UploadCustomQR($input: UploadCustomQRInput!) {
    uploadCustomQR(input: $input) {
      customQRCode {
        id
        assetLinkId
        filename
        s3Key
        downloadUrl
        contentType
        size
        uploadedAt
      }
      uploadUrl
      uploadFields
    }
  }
`;

export const DELETE_CUSTOM_QR = gql`
  mutation DeleteCustomQR($id: ID!) {
    deleteCustomQR(id: $id)
  }
`;
